import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { ITableColumnSetting } from '../../shared/table-preferences.service';

/**
 * Reusable column-settings popover: a trigger icon that opens a panel listing
 * the visible columns (draggable, vertically reorderable) and the hidden
 * columns. Toggling the eye moves a column between the two lists.
 *
 * Stateless w.r.t. persistence — the host owns the column array and persists
 * via `TablePreferencesService`. Every change emits the full ordered list
 * (visible columns first in display order, then hidden) through
 * `columnsChange`.
 */
@Component({
  selector: 'ui-table-column-settings',
  standalone: true,
  imports: [CommonModule, DragDropModule, MatTooltipModule, TranslateModule, SvgIconComponent],
  templateUrl: './table-column-settings.component.html',
  styleUrls: ['./table-column-settings.component.scss'],
})
export class TableColumnSettingsComponent {
  /** Ordered, role-allowed columns. Host filters role-gated columns out. */
  @Input() set columns(value: ITableColumnSetting[]) {
    const list = (value ?? []).map((column) => ({ ...column }));
    // Pinned columns are anchored to the front (immovable, nothing before them).
    // The rest stay in their persisted order and are reorderable.
    this.pinnedColumns = list.filter((column) => column.visible && column.pinned);
    this.reorderColumns = list.filter((column) => column.visible && !column.pinned);
    this.hiddenColumns = list.filter((column) => !column.visible);
  }

  /** Svg icon name (from `assets/icones`) used for the trigger button. */
  @Input() triggerIcon = 'settings';

  /**
   * Optional hard cap on how many columns can be visible at once. `null` (the
   * default) means uncapped, so every existing table usage is unaffected. When
   * set, the panel states the limit and the show-toggles are disabled once it is
   * reached — used by the quick-actions row, which must never wrap.
   */
  @Input() maxVisible: number | null = null;

  /** Emits the full ordered column list whenever order or visibility changes. */
  @Output() columnsChange = new EventEmitter<ITableColumnSetting[]>();

  open = false;
  /** Fixed-position coordinates for the popover (escapes table stacking/overflow). */
  panelTop = '0px';
  panelRight = '0px';
  /** Pinned, immovable columns rendered first. */
  pinnedColumns: ITableColumnSetting[] = [];
  /** Reorderable columns (the cdkDropList). */
  reorderColumns: ITableColumnSetting[] = [];
  hiddenColumns: ITableColumnSetting[] = [];

  /** A column can be hidden unless explicitly flagged otherwise. */
  isHideable(column: ITableColumnSetting): boolean {
    return column.hideable !== false;
  }

  /** Number of columns currently shown (pinned ones count toward the cap). */
  get visibleCount(): number {
    return this.pinnedColumns.length + this.reorderColumns.length;
  }

  /** True when a `maxVisible` cap is set and already reached. */
  get atCapacity(): boolean {
    return this.maxVisible !== null && this.visibleCount >= this.maxVisible;
  }

  constructor() {}

  toggle(event?: MouseEvent): void {
    this.open = !this.open;
    if (this.open && event) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.panelTop = `${rect.bottom + 6}px`;
      this.panelRight = `${window.innerWidth - rect.right}px`;
    }
  }

  @HostListener('document:keydown.escape')
  close(): void {
    this.open = false;
  }

  onDrop(event: CdkDragDrop<ITableColumnSetting[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.reorderColumns, event.previousIndex, event.currentIndex);
    this.emitChange();
  }

  hide(column: ITableColumnSetting): void {
    if (!this.isHideable(column)) {
      return;
    }
    this.pinnedColumns = this.pinnedColumns.filter((item) => item.key !== column.key);
    this.reorderColumns = this.reorderColumns.filter((item) => item.key !== column.key);
    this.hiddenColumns = [...this.hiddenColumns, { ...column, visible: false }];
    this.emitChange();
  }

  show(column: ITableColumnSetting): void {
    this.hiddenColumns = this.hiddenColumns.filter((item) => item.key !== column.key);
    const shown = { ...column, visible: true };
    // Restore a pinned column to the pinned group; everything else is reorderable.
    if (shown.pinned) {
      this.pinnedColumns = [...this.pinnedColumns, shown];
    } else {
      this.reorderColumns = [...this.reorderColumns, shown];
    }
    this.emitChange();
  }

  private emitChange(): void {
    this.columnsChange.emit([
      ...this.pinnedColumns,
      ...this.reorderColumns,
      ...this.hiddenColumns,
    ]);
  }
}
